// @flow
import React, { useState } from 'react'
import FolderTreePicker from './FolderTreePicker'
import { labelPath } from './path'
import styles from './styles.css'
import type {
  DigitalFile as DigitalFileType,
  TemplateNode,
} from 'lib/digital_file/model'

type Props = {
  file: DigitalFileType,
  template: Array<TemplateNode>,
  onCancel: () => void,
  onConfirm: (path: string) => mixed,
  onArchive?: () => mixed,
}

const MoveDialog = ({ file, template, onCancel, onConfirm, onArchive }: Props) => {
  const [target, setTarget] = useState<?string>(null)
  const [submitting, setSubmitting] = useState(false)

  const fromLegacy = file.path === 'legacy'
  const canMove = !!target && target !== file.path && !submitting

  const submit = async () => {
    if (!canMove || !target) return
    setSubmitting(true)
    try {
      await onConfirm(target)
    } catch (_err) {
      setSubmitting(false)
    }
  }

  return (
    <div className={styles.moveOverlay} role="dialog" aria-modal="true">
      <div className={styles.moveBackdrop} onClick={onCancel} />
      <div className={styles.moveCard}>
        <div className={styles.moveHeader}>
          <div className={styles.moveTitle}>Move file</div>
          <div className={styles.moveSubtitle}>{file.name}</div>
          {fromLegacy ? (
            <div className={styles.moveLegacyCaption}>
              Currently in Legacy queue
            </div>
          ) : (
            <div className={styles.moveCurrent}>
              From: {labelPath(template, file.path) || '—'}
            </div>
          )}
          {file.legacy_path != null && (
            <div className={styles.moveCurrent}>
              Original: {file.legacy_path}
            </div>
          )}
        </div>
        <div className={styles.moveBody}>
          <div className={styles.moveBodyLabel}>Choose a destination folder</div>
          <FolderTreePicker
            template={template}
            currentPath={file.path}
            target={target}
            onSelect={setTarget}
          />
        </div>
        <div className={styles.moveFooter}>
          <div className={styles.moveFooterLeft}>
            {fromLegacy && onArchive && (
              <button
                type="button"
                className={styles.moveArchive}
                onClick={onArchive}
                disabled={submitting}
              >
                Archive
              </button>
            )}
          </div>
          <div className={styles.moveFooterRight}>
            <button
              type="button"
              className={styles.moveCancel}
              onClick={onCancel}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="button"
              className={styles.moveConfirm}
              onClick={submit}
              disabled={!canMove}
            >
              {submitting ? 'Moving…' : 'Move'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default MoveDialog
